import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useGTMEvent } from 'hooks/UseGTM';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Services
import { DashboardHttpService } from 'services/http';
import { getStartDate, getEndDate } from 'services/dashboard';
// Import UI Components
import { DatePickerInput, BusyLoader, BarChart, Empty, ModuleTitle } from '@geneui/components';
// Import Constants
import { GACategoryEvents, l } from 'constants/common';
// Import Helpers
import { customMomentWithoutTimezoneConversion } from 'services/Helpers';

const { GADateFilterCategory } = GACategoryEvents;

const DailyActiveCustomersByProductChart = ({ parentDate }) => {
    const { doGetRequest } = useRequest();
    const { t } = useTranslation();
    const { logEvent } = useGTMEvent();
    const [isLoading, setIsLoading] = useState(false);
    const [dashboardClientActivityData, setDashboardClientActivity] =
        useState<Nullable<{ series: { name?: string; data: number[] }[]; categories: string[] }>>(null);
    const [date, setDate] = useState(parentDate);
    const { partnerDateFormat } = usePartnerDateFormats();

    const { getDashboardClientActivityRequest } = useCancelableRequests(
        { getDashboardClientActivityRequest: DashboardHttpService.getDashboardClientActivity },
        { takeLatest: ['getDashboardClientActivityRequest'] },
    );

    const getDashboardClientActivity = (date) => {
        setIsLoading(true);

        doGetRequest(getDashboardClientActivityRequest.request, {
            queryString: { startDate: date[0], endDate: date[1] },
            successCallback: ({ StackBarChartData, SportEventsSeries }) => {
                interface StackBarChartDataItem {
                    Categories: string[];
                    CasinoClientCounts: number[];
                    ClientCounts: number[];
                    SportClientCounts: number[];
                }

                const stackBarChartData = StackBarChartData.reduce<StackBarChartDataItem>(
                    (acc, element) => {
                        acc.CasinoClientCounts.push(element.CasinoClientCount);
                        acc.ClientCounts.push(element.ClientCount);
                        acc.SportClientCounts.push(element.SportClientCount);
                        acc.Categories.push(customMomentWithoutTimezoneConversion(element.Date).format('MMM DD'));
                        return acc;
                    },
                    {
                        Categories: [],
                        CasinoClientCounts: [],
                        ClientCounts: [],
                        SportClientCounts: [],
                    },
                );
                setDashboardClientActivity({
                    series: [
                        {
                            name: SportEventsSeries.find((r) => r.field === 'CasinoClientCount')?.name,
                            data: stackBarChartData.CasinoClientCounts,
                        },
                        {
                            name: SportEventsSeries.find((r) => r.field === 'ClientCount')?.name,
                            data: stackBarChartData.ClientCounts,
                        },
                        {
                            name: SportEventsSeries.find((r) => r.field === 'SportClientCount')?.name,
                            data: stackBarChartData.SportClientCounts,
                        },
                    ],
                    categories: stackBarChartData.Categories,
                });
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoading(false);
            }
        });
    };

    const getDate = (date) => [getStartDate(date[0]), getEndDate(date[1])];

    const handleDatePickerChange = (e) => {
        const date = getDate(e);

        logEvent(
            GADateFilterCategory.name,
            GADateFilterCategory.events.activeCustomersByProductCategories,
            GADateFilterCategory.labels.activeCustomersByProductCategories,
            `${date[0]} - ${date[1]}`,
        );
        setDate(date);
    };

    useEffect(() => {
        getDashboardClientActivity(date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date[0], date[1]]);

    useEffect(() => {
        setDate(getDate(parentDate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentDate]);

    return (
        <>
            <div className="charts-wrap-inner-head">
                <ModuleTitle title={t(l.DailyActiveCustomersByProductCategories)} />

                <div className="campaign-metrics-wrap-c-h-d">
                    <DatePickerInput.WithRange
                        onChange={handleDatePickerChange}
                        label={t(l.TimePeriod)}
                        labelAppearance="swap"
                        value={date}
                        format={partnerDateFormat}
                    />
                </div>
            </div>
            {isLoading ? (
                <BusyLoader spinnerSize="big" isBusy={true} />
            ) : isEmpty(dashboardClientActivityData?.categories) ? (
                <Empty title={t(l.NoDataToDisplay)} />
            ) : (
                <div className="charts-wrap-inner-content">
                    <BarChart
                        series={dashboardClientActivityData?.series ?? []}
                        categories={dashboardClientActivityData?.categories ?? []}
                        plotOptions={{
                            column: {
                                stacking: 'normal',
                            },
                        }}
                    />
                </div>
            )}
        </>
    );
};

DailyActiveCustomersByProductChart.propTypes = {
    parentDate: PropTypes.array.isRequired,
};

export default DailyActiveCustomersByProductChart;
